import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Mail, ShoppingBag, Instagram, MessageCircle } from "lucide-react";
import { LINKS, CONTACT } from "../data/site";
import MaskReveal from "./MaskReveal";

const EASE = [0.16, 1, 0.3, 1];

const CARDS = [
  {
    icon: MapPin,
    title: "Endereço",
    body: (
      <a href={LINKS.maps} target="_blank" rel="noreferrer" data-testid="contact-maps-link" className="leading-relaxed text-white/85 underline decoration-pinky/60 decoration-2 underline-offset-4 transition-colors hover:text-pinky">
        {CONTACT.address}
      </a>
    ),
  },
  {
    icon: Clock,
    title: "Horário de funcionamento",
    body: (
      <div className="space-y-1">
        <p className="text-white/85">Seg – Sex: 9:00 às 18:00</p>
        <p className="text-white/85">Sábado: 9:00 às 14:00</p>
      </div>
    ),
  },
  {
    icon: Phone,
    title: "Telefones",
    body: (
      <div className="space-y-1">
        {CONTACT.phones.map(([label, href]) => (
          <a key={href} href={href} data-testid="contact-phone-link" className="block text-white/85 transition-colors hover:text-pinky">
            {label}
          </a>
        ))}
      </div>
    ),
  },
  {
    icon: Mail,
    title: "E-mail",
    body: (
      <a href={`mailto:${CONTACT.email}`} data-testid="contact-email-link" className="break-all leading-relaxed text-white/85 transition-colors hover:text-pinky">
        {CONTACT.email}
      </a>
    ),
  },
];

const SOCIALS = [
  { href: LINKS.shopee, icon: ShoppingBag, label: "Ver lojinha na Shopee", testid: "contact-shopee-link", className: "bg-shopee hover:bg-shopeedark shadow-[0_14px_34px_rgba(238,77,45,0.45)]" },
  { href: LINKS.instagram, icon: Instagram, label: "Seguir no Instagram", testid: "contact-instagram-link", className: "bg-gradient-to-r from-instagram via-pink-500 to-lilac shadow-[0_14px_34px_rgba(225,48,108,0.4)]" },
  { href: LINKS.whatsapp, icon: MessageCircle, label: "Conversar no WhatsApp", testid: "contact-whatsapp-link", className: "bg-wa hover:bg-wadark shadow-[0_14px_34px_rgba(37,211,102,0.4)]" },
];

export default function Contact() {
  return (
    <section id="contato" className="relative overflow-hidden bg-ink py-20 text-white sm:py-28" data-testid="contact-section">
      <div className="absolute -left-32 top-0 h-96 w-96 rounded-full bg-lilac/25 blur-[130px]" />
      <div className="absolute -right-24 bottom-0 h-96 w-96 rounded-full bg-pinky/20 blur-[130px]" />
      <div className="absolute left-1/2 top-10 -z-0 text-goldy/40"><span className="animate-twinkle block text-2xl">✦</span></div>

      <div className="relative mx-auto max-w-7xl px-5 sm:px-8">
        <div className="mx-auto mb-14 max-w-2xl text-center">
          <motion.span
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, ease: EASE }}
            className="mb-4 inline-block rounded-full border-2 border-white/15 bg-white/5 px-4 py-1.5 text-xs font-extrabold uppercase tracking-[0.2em] text-goldy"
          >
            Fale com a gente
          </motion.span>
          <MaskReveal delay={0.1}>
            <h2 className="font-display text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
              Vem encher o mundo de fofura
            </h2>
          </MaskReveal>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.3 }}
            className="mt-3 font-hand text-2xl text-white/70"
          >
            encomendas, dúvidas e mimos: chama aqui!
          </motion.p>
        </div>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
          {CARDS.map(({ icon: Icon, title, body }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 36 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.2 }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.1 }}
              className="rounded-3xl border-2 border-white/10 bg-white/5 p-6 backdrop-blur transition-colors duration-300 hover:border-pinky/50"
              data-testid={`contact-card-${i}`}
            >
              <span className="mb-4 grid h-12 w-12 place-items-center rounded-2xl bg-gradient-to-tr from-pinky/30 to-lilac/30 text-pinky">
                <Icon size={20} />
              </span>
              <h3 className="mb-2 font-display text-lg font-bold">{title}</h3>
              <div className="text-sm font-medium">{body}</div>
            </motion.div>
          ))}
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8, ease: EASE, delay: 0.2 }}
          className="mt-14 flex flex-wrap items-center justify-center gap-4"
        >
          {SOCIALS.map(({ href, icon: Icon, label, testid, className }) => (
            <a
              key={testid}
              href={href}
              target="_blank"
              rel="noreferrer"
              data-testid={testid}
              className={`flex items-center gap-2.5 rounded-full px-8 py-4 text-base font-extrabold text-white transition-transform duration-300 hover:scale-105 active:scale-95 ${className}`}
            >
              <Icon size={19} />
              {label}
            </a>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
